// Mirrors backend/core/languages.py — keep the two in sync. Distinguishes
// the APPLICATION language code (stored, sent to the API, used as the
// OpenAI translation instruction) from the provider-specific Google STT
// locale, which is region-qualified (e.g. "hi-IN", not "hi") and only
// exists for a subset of these languages.
//
// NOT the authoritative source of which languages the ACTIVE backend
// provider supports — that comes from GET /api/capabilities/languages
// (see services/capabilitiesApi.js), which reflects the real,
// currently-configured STT/translation/TTS providers. This list is now
// only: helper labels, application-code normalization, the "auto" and
// "same_as_translation" sentinels, and a loading-state/offline fallback
// for pages that fetch capabilities (StartMeetingPage, SettingsPage) —
// used only until that fetch resolves, then replaced.
export const LANGUAGES = [
  { code: 'en', label: 'English (US)', googleSttCode: 'en-US' },
  { code: 'en-GB', label: 'English (UK)', googleSttCode: 'en-GB' },
  { code: 'hi', label: 'Hindi', googleSttCode: 'hi-IN' },
  { code: 'gu', label: 'Gujarati', googleSttCode: 'gu-IN' },
  { code: 'es', label: 'Spanish', googleSttCode: 'es-ES' },
  { code: 'fr', label: 'French', googleSttCode: 'fr-FR' },
  { code: 'de', label: 'German', googleSttCode: 'de-DE' },
  { code: 'pt', label: 'Portuguese', googleSttCode: 'pt-PT' },
  { code: 'ar', label: 'Arabic', googleSttCode: 'ar-SA' },
  { code: 'zh', label: 'Chinese (Mandarin)', googleSttCode: 'cmn-Hans-CN' },
  { code: 'ja', label: 'Japanese', googleSttCode: 'ja-JP' },
  { code: 'ko', label: 'Korean', googleSttCode: 'ko-KR' },
  { code: 'it', label: 'Italian', googleSttCode: 'it-IT' },
  // Sarvam AI's Indian-language set (see backend/core/languages.py for
  // why these have no googleSttCode): usable for translation/Sarvam STT
  // but not offered as a Google STT source.
  { code: 'bn', label: 'Bengali' },
  { code: 'kn', label: 'Kannada' },
  { code: 'ml', label: 'Malayalam' },
  { code: 'mr', label: 'Marathi' },
  { code: 'or', label: 'Odia' },
  { code: 'pa', label: 'Punjabi' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'as', label: 'Assamese' },
  { code: 'ur', label: 'Urdu' },
  { code: 'ne', label: 'Nepali' },
  { code: 'kok', label: 'Konkani' },
  { code: 'ks', label: 'Kashmiri' },
  { code: 'sd', label: 'Sindhi' },
  { code: 'sa', label: 'Sanskrit' },
  { code: 'sat', label: 'Santali' },
  { code: 'mni', label: 'Manipuri (Meitei)' },
  { code: 'brx', label: 'Bodo' },
  { code: 'mai', label: 'Maithili' },
  { code: 'doi', label: 'Dogri' },
];

// Every language above currently has a Google STT locale, but a future
// translation-only addition (no live STT support) would set
// googleSttCode to null/undefined — this filter is what keeps such a
// language out of "Client Language" (live source) pickers while still
// letting it appear as a "Translate To" (target) option, since only
// SOURCE goes through Google STT.
export const STT_SOURCE_LANGUAGES = LANGUAGES.filter(
  (lang) => !!lang.googleSttCode
);

// Sentinel meetings.source_language value meaning "auto-detect /
// multilingual" rather than one fixed spoken language — mirrors
// backend/core/languages.py's SOURCE_LANGUAGE_AUTO exactly. Every
// currently configured STT provider (Google, Sarvam) supports some
// form of automatic/multilingual recognition — see that file's
// google_stt_config() and backend/services/speech/languages.py's
// SARVAM_LANGUAGE_MAP for how each provider adapter resolves it.
export const SOURCE_LANGUAGE_AUTO = 'auto';

// "Spoken Language" picker options (Start Meeting / Settings): Auto/
// Multilingual first (the recommended default for this app's mixed
// Hindi/Gujarati/English interview use case), then every STT-capable
// specific language. Deliberately a separate list from
// STT_SOURCE_LANGUAGES rather than mutating it, since "auto" is a mode
// selector, not a language with its own STT locale.
export const SPOKEN_LANGUAGE_OPTIONS = [
  { code: SOURCE_LANGUAGE_AUTO, label: 'Auto / Multilingual' },
  ...STT_SOURCE_LANGUAGES,
];

// Sentinel meetings.ai_answer_language value meaning "use whatever the
// meeting's translation target_language is" — mirrors backend/core/
// languages.py's AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION. Default so a
// user is never forced to pick the same language twice.
export const AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION = 'same_as_translation';

export const AI_ANSWER_LANGUAGE_OPTIONS = [
  { value: AI_ANSWER_LANGUAGE_SAME_AS_TRANSLATION, label: 'Same as Translation' },
  ...LANGUAGES.map((lang) => ({ value: lang.code, label: lang.label })),
];

// models/meeting.py's MeetingMode - shared with Desktop (@copilot/ui).
export { CO_PILOT_MODES } from '@copilot/ui';
